import React, { useMemo, useState } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import { Reveal, ExternalLinkIcon, SectionLabel } from "./primitives";
import { faqs, businessDetails } from "../data/content";
import { track } from "../lib/analytics";

const ext = { target: "_blank", rel: "noopener noreferrer" };

const InlineAction = ({ action }) => {
  if (action === "app") {
    return (
      <a
        {...ext}
        href={businessDetails.customerAppUrl}
        onClick={() => track("customer_app_clicked", { location: "faq" })}
        className="mt-3 inline-flex min-h-[44px] items-center justify-center gap-2 rounded-full bg-brand-teal px-5 font-semibold text-white transition-colors hover:bg-brand-tealHover"
        data-testid="faq-open-app"
        aria-label="Open Customer App — opens external customer service in a new tab"
      >
        Open Customer App <ExternalLinkIcon />
      </a>
    );
  }
  if (action === "postbox") {
    return (
      <a
        {...ext}
        href={businessDetails.digitalPostboxUrl}
        onClick={() => track("digital_postbox_clicked", { location: "faq" })}
        className="mt-3 inline-flex min-h-[44px] items-center justify-center gap-2 rounded-full border border-brand-navy px-5 font-semibold text-brand-navy transition-colors hover:bg-brand-navy hover:text-white"
        data-testid="faq-open-postbox"
        aria-label="Open Digital Postbox — opens external customer service in a new tab"
      >
        Open Digital Postbox <ExternalLinkIcon />
      </a>
    );
  }
  if (action === "complaints") {
    return (
      <a
        href="#complaints"
        onClick={() => track("complaints_policy_clicked", { location: "faq" })}
        className="mt-3 inline-flex min-h-[44px] items-center justify-center gap-2 rounded-full border border-brand-navy px-5 font-semibold text-brand-navy transition-colors hover:bg-brand-navy hover:text-white"
        data-testid="faq-complaints-link"
      >
        Go to Complaints
      </a>
    );
  }
  return null;
};

const FaqColumn = ({ items, colKey }) => (
  <Accordion type="single" collapsible className="space-y-3">
    {items.map(({ faq, i }) => (
      <AccordionItem
        key={faq.q}
        value={`faq-${i}`}
        className="overflow-hidden rounded-2xl border border-brand-border bg-white px-5 transition-shadow data-[state=open]:shadow-md"
      >
        <AccordionTrigger
          onClick={() => track("faq_opened", { label: `faq-${i}` })}
          className="py-5 text-left font-heading text-base font-bold text-brand-navy hover:no-underline lg:text-lg"
          data-testid={`faq-trigger-${i}`}
        >
          {faq.q}
        </AccordionTrigger>
        <AccordionContent className="pb-5 text-base leading-relaxed text-brand-body">
          <p>{faq.a}</p>
          <InlineAction action={faq.action} />
        </AccordionContent>
      </AccordionItem>
    ))}
  </Accordion>
);

export const FaqSection = () => {
  const categories = useMemo(() => {
    const seen = [];
    faqs.forEach((f) => {
      if (f.category && !seen.includes(f.category)) seen.push(f.category);
    });
    return ["All", ...seen];
  }, []);

  const [active, setActive] = useState("All");

  const filtered = useMemo(
    () =>
      faqs
        .map((faq, i) => ({ faq, i }))
        .filter(({ faq }) => active === "All" || faq.category === active),
    [active]
  );

  const left = filtered.filter((_, idx) => idx % 2 === 0);
  const right = filtered.filter((_, idx) => idx % 2 === 1);

  return (
    <section id="faqs" aria-labelledby="faqs-heading" className="bg-brand-bg py-16 lg:py-24">
      <div className="mx-auto max-w-content px-4 lg:px-8">
        <Reveal className="max-w-3xl">
          <SectionLabel>FAQs</SectionLabel>
          <h2
            id="faqs-heading"
            className="mt-3 font-heading text-3xl font-bold tracking-tight text-brand-navy lg:text-4xl"
          >
            Frequently asked questions
          </h2>
          <p className="mt-3 text-lg leading-relaxed text-brand-body">
            Browse by topic to find answers about IVAs and managing your Freeman Jones
            arrangement.
          </p>
        </Reveal>

        {/* Category filter */}
        <Reveal delay={0.05} className="mt-8">
          <div
            role="tablist"
            aria-label="FAQ categories"
            className="flex flex-wrap gap-2"
          >
            {categories.map((cat) => {
              const isActive = cat === active;
              return (
                <button
                  key={cat}
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActive(cat)}
                  className={`inline-flex min-h-[44px] items-center rounded-full border px-5 text-sm font-semibold transition-colors ${
                    isActive
                      ? "border-brand-navy bg-brand-navy text-white"
                      : "border-brand-border bg-white text-brand-navy hover:border-brand-teal hover:text-brand-teal"
                  }`}
                  data-testid={`faq-cat-${cat.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                >
                  {cat}
                </button>
              );
            })}
          </div>
        </Reveal>

        <div className="mt-8 grid items-start gap-3 md:grid-cols-2 md:gap-4">
          <FaqColumn items={left} colKey="l" />
          <FaqColumn items={right} colKey="r" />
        </div>

        <p className="mt-8 max-w-3xl text-sm leading-relaxed text-brand-body">
          The information provided here is general and is given in contemplation of an insolvency
          appointment. It is not personal financial or legal advice — the suitability and effect
          of an IVA depend on your individual circumstances.
        </p>
      </div>
    </section>
  );
};
